import React, { memo, useState, useEffect } from "react";
import { Modal, Box, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IconPlus } from "../../../icons/Icon";
import { IconDelete } from "../../../icons/Icon";
import { toast } from 'react-toastify';

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 368,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "6px",
  outline: "none",
};

interface InfoModalProps {
  open: boolean;
  handleClose: () => void;
  handleOk?: (inputValues: string[]) => void;
  handleAddCafe?: () => void;
  title?: string;
  closeText?: string;
  submitText?: string;
  noOfCafes: number;
  cafeList?: any;
}

const DefineCafeModal: React.FC<InfoModalProps> = ({
  open,
  handleClose,
  handleOk,
  title = "Default Title",
  closeText,
  submitText,
  noOfCafes,
  cafeList
}) => {
  const [inputValues, setInputValues] = useState<string[]>(
    new Array(noOfCafes).fill("")
  );


  useEffect(() => {
    setInputValues(new Array(noOfCafes).fill(""));
  }, [noOfCafes]);

  const handleInputChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValues = [...inputValues];
    newInputValues[index] = event.target.value; 
    setInputValues(newInputValues);
  };

  // Add a new input box
  const handleAddInput = () => {
    setInputValues([...inputValues, ""]); 
  };


  // Delete a specific input box
  const handleDeleteInput = (index: number) => {
    const newInputValues = [...inputValues];
    newInputValues.splice(index, 1); // Remove the input at the given index
    setInputValues(newInputValues);
  };


  const handleSubmit = () => {
    const trimmedValues = inputValues.map((value) => value.trim().toLowerCase());
    if (trimmedValues.some((value) => value === "")) {
        toast("Please fill out all fields before submitting.", { type: "warning" });
        return;
    }
    const uniqueValues = new Set(trimmedValues);
    if (uniqueValues.size !== trimmedValues.length) {
      toast("Duplicate cafe names are not allowed.", { type: "warning" });
      return;
    }
    const cafeLabelsSet = new Set(cafeList?.map((cafe: any) => cafe.label.toLowerCase()) || []);
    if (trimmedValues.some((name) => cafeLabelsSet.has(name))) {
      toast("This cafe name already exists. Please choose a different name.", { type: "warning" });
      return;
    }
    if (handleOk) {
      handleOk(inputValues.map((value) => value.trim()));
    }
    handleClose();
  };
  

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={modalStyle} className="modal">
          <div className="modal-header flex justify-content-between align-items-center">
            {title && <h3 className="modal-title">{title}</h3>}
            <button className="close" onClick={handleClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="modal-body">
            {inputValues.map((inputValue, index) => (
              <div
                key={index}
                className="flex justify-content-between align-items-center mb-12"
              >
                <div className="input-wrap">
                  <label htmlFor={`input-rows-${index}`}>
                    Cafe {index + 1}
                    <span className="rows-mandatory"> *</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Type Cafe Name"
                    id={`input-rows-${index}`}
                    className="input-rows"
                    value={inputValue}
                    onChange={(e) => handleInputChange(index, e)}
                  />
                </div>
                <button
                  style={{ paddingLeft: "10px", marginTop: "30px" }}
                  onClick={() => handleDeleteInput(index)}
                >
                  <IconDelete />
                </button>
              </div>
            ))}
            <div className="flex align-items-center add-cafe">
              <button type="button" onClick={handleAddInput}>
                Add cafe <span>
                  <IconPlus />
                </span>
              </button>
            </div>
          </div>
          <div className="modal-footer flex justify-content-center align-items-center">
            {closeText && (
              <Button onClick={handleClose} className="border-btn">
                {closeText}
              </Button>
            )}
            {submitText && (
              <Button className="primary-btn" onClick={handleSubmit}>
                {submitText}
              </Button>
            )}
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default memo(DefineCafeModal);
